import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { authService } from '@uniqcamp/shared';
import apiClient from '@uniqcamp/shared/services/api.service';
import { useToast } from '../../contexts/ToastContext';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { AUTHORITY_LEVELS } from '../../constants';
import type { SchoolAuthorityLevelsResponse, SchoolsResponse } from '../../types';

type Row = { value: string; label: string; order: number };

function rowsFromSchool(raw: unknown): Row[] {
  if (Array.isArray(raw) && raw.length > 0) {
    return [...raw]
      .map((x: { value?: string; label?: string; order?: number }, i: number) => ({
        value: String(x?.value ?? '').trim(),
        label: String(x?.label ?? x?.value ?? '').trim(),
        order: Number.isFinite(Number(x?.order)) ? Number(x.order) : i,
      }))
      .filter((r) => r.value)
      .sort((a, b) => a.order - b.order || a.value.localeCompare(b.value));
  }
  return AUTHORITY_LEVELS.map((l, i) => ({ value: l.value, label: l.label, order: i }));
}

export default function AuthorityLevelsTab() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { canEdit } = useFeatureAccess('staffRoles');
  const [rows, setRows] = useState<Row[]>([]);

  const { data: schoolsData } = useQuery<SchoolsResponse>('schools', async () => {
    const res = await apiClient.get('/schools');
    return res.data as SchoolsResponse;
  });
  const school = schoolsData?.schools?.[0];
  const schoolId = authService.getStoredUser()?.schoolId || school?._id;
  const { data: authorityData } = useQuery<SchoolAuthorityLevelsResponse>(
    ['school-authority-levels', schoolId],
    async () => {
      const res = await apiClient.get(`/schools/${schoolId}/authority-levels`);
      return res.data as SchoolAuthorityLevelsResponse;
    },
    { enabled: !!schoolId }
  );

  useEffect(() => {
    if (authorityData?.levels?.length) {
      setRows(rowsFromSchool(authorityData.levels));
      return;
    }
    setRows(rowsFromSchool(school?.settings?.authorityLevels));
  }, [authorityData?.levels, school?._id, school?.settings?.authorityLevels]);

  const saveMutation = useMutation(
    async (payload: { _id: string; settings: Record<string, unknown> }) => {
      const res = await apiClient.put(`/schools/${payload._id}`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schools');
        if (schoolId) queryClient.invalidateQueries(['school-authority-levels', schoolId]);
        showToast('success', 'Authority levels saved.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to save');
      },
    }
  );

  const handleSave = () => {
    if (!school) return;
    const seen = new Set<string>();
    for (const r of rows) {
      if (!/^[A-Za-z0-9_-]{1,48}$/.test(r.value)) {
        showToast('error', `Invalid code "${r.value}": use letters, numbers, _ or - (max 48 chars).`);
        return;
      }
      if (seen.has(r.value)) {
        showToast('error', `Duplicate code: ${r.value}`);
        return;
      }
      seen.add(r.value);
    }
    const authorityLevels = rows.map((r, i) => ({
      value: r.value.trim(),
      label: (r.label || r.value).trim(),
      order: Number.isFinite(r.order) ? r.order : i,
    }));
    saveMutation.mutate({
      _id: school._id,
      settings: { ...school.settings, authorityLevels },
    });
  };

  const handleResetDefaults = () => {
    setRows(AUTHORITY_LEVELS.map((l, i) => ({ value: l.value, label: l.label, order: i })));
  };

  const handleClearCustom = () => {
    if (!school) return;
    saveMutation.mutate({
      _id: school._id,
      settings: { ...school.settings, authorityLevels: [] },
    });
    setRows(AUTHORITY_LEVELS.map((l, i) => ({ value: l.value, label: l.label, order: i })));
  };

  const updateRow = (index: number, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const move = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= rows.length) return;
    setRows((prev) => {
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next.map((r, i) => ({ ...r, order: i }));
    });
  };

  const handleAdd = () => {
    const n = rows.length + 1;
    setRows((prev) => [...prev, { value: `LEVEL_${n}`, label: `Level ${n}`, order: prev.length }]);
  };

  const handleDelete = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index).map((r, i) => ({ ...r, order: i })));
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Staff <strong>roles</strong> describe responsibilities (teacher, driver, etc.). Authority <strong>levels</strong>
        describe seniority/hierarchy. Define your school&apos;s authority ladder from <strong>top</strong> (first row) to
        <strong> base</strong> (last row). Staff pick a level when added; new staff default to the bottom row. Use
        stable codes (e.g. LEVEL_6) — changing a code later does not update existing staff automatically.
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {canEdit && (
            <>
              <Button variant="contained" onClick={handleSave} disabled={saveMutation.isLoading}>
                Save levels
              </Button>
              <Button variant="outlined" onClick={handleResetDefaults}>
                Reset form to defaults
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleClearCustom}
                disabled={saveMutation.isLoading || !school?.settings?.authorityLevels?.length}
              >
                Clear saved custom (use app defaults)
              </Button>
              <Button startIcon={<AddIcon />} onClick={handleAdd}>
                Add level
              </Button>
            </>
          )}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={48}>Order</TableCell>
                <TableCell>Code (stored)</TableCell>
                <TableCell>Label (shown in UI)</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={`${r.value}-${i}`}>
                  <TableCell>
                    {canEdit ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => move(i, -1)} disabled={i === 0}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => move(i, 1)} disabled={i === rows.length - 1}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      i + 1
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={r.value}
                        onChange={(e) => updateRow(i, { value: e.target.value })}
                        placeholder="e.g. LEVEL_6"
                      />
                    ) : (
                      r.value
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={r.label}
                        onChange={(e) => updateRow(i, { label: e.target.value })}
                      />
                    ) : (
                      r.label
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleDelete(i)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
